import { Delete, Edit } from "@mui/icons-material";
import {
  Box,
  Button,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import Alert from "components/alert";
import Note from "components/common/note";
import TableSkeleton from "components/skeletons/TableSkeleton";
import TablePagination from "components/tablePagination";
import PopoverAG from "components/ui/popoverAG";
import { updateDueDate } from "features/invoices/invoices/api/invoices";
import { deleteInvoice } from "features/projectCustomers/api/projectCustomer";
import moment from "moment";
import { Fragment, useState } from "react";
import { BsChevronExpand } from "react-icons/bs";
import { MdEdit, MdOutlinePayment } from "react-icons/md";
import { TbListDetails } from "react-icons/tb";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import DateColumn from "../DateColumn";
import Items from "./Items";
import StatusColumn from "../StatusColumn";
import { invoiceStatuses } from "utils/constants";

const InvoiceTable = ({
  loading,
  allInvoice,
  pagination,
  setPagination,
  setReload,
  setAddInvoice,
  setCurrInvoice,
  setAllInvoice,
  bgColor,
  setMakePayment,
  setViewInvoice,
  module_name,
  permissions,
  handleDone = () => {},
}) => {
  const dispatch = useDispatch();
  const [openItems, setOpenItems] = useState([]);
  const rows = allInvoice;

  const columns = [
    { id: "invoice_number", label: "Invoice ID" },
    { id: "total", label: "Total", align: "right" },
    { id: "due_payment", label: "Due Amount", align: "right" },
    { id: "paid_amount", label: "Paid Amount", align: "right" },
    { id: "due_date", label: "Due Date", style: { width: "110px" } },

    { id: "assignees", label: "Assigned", align: "center" },
    {
      id: "invoice_details",
      label: "Items",
      align: "center",
      style: { width: "140px" },
    },
    { id: "last_note", label: "Notes", style: { width: "190px" } },
    { id: "status", label: "Status", align: "center" },
    {
      id: "action",
      label: "Actions",
      style: { width: "150px" },
      align: "center",
    },
  ];

  const [openAlert, setOpenAlert] = useState(false);
  const [openNote, setOpenNote] = useState("");

  const handleDelete = async (id) => {
    setOpenAlert(id);
  };

  const handleDeleteConfirm = async () => {
    const result = await deleteInvoice({ id: openAlert });
    if (result.success) {
      handleDone();
      toast.success(result.message);
      setAllInvoice(allInvoice.filter((invoice) => invoice.id !== openAlert));
    } else {
      toast.error(result.message);
    }
    setOpenAlert(false);
  };

  const handleEditBtn = (value) => {
    setCurrInvoice(value);
    setAddInvoice(true);
  };

  const handlePaymentBtn = (value) => {
    setCurrInvoice(value);
    setMakePayment(true);
  };

  return (
    <Fragment>
      {openAlert && (
        <Alert
          open={openAlert}
          handleClose={setOpenAlert}
          title="Delete Invoice"
          desc={
            <Box>
              Are you sure you want to delete? This will delete this Invoice
              permanently. <strong>You can't undo this action</strong>
            </Box>
          }
          buttons={
            <>
              <Button onClick={() => setOpenAlert(false)}>No, keep it</Button>
              <Button variant="solid" onClick={handleDeleteConfirm}>
                Yes, delete it
              </Button>
            </>
          }
        />
      )}

      {openNote && (
        <Note
          open={openNote}
          handleClose={() => setOpenNote(false)}
          id={openNote}
          related_id={allInvoice.find((c) => c?.id === openNote)?.id}
          related_to="invoice"
          route="invoice"
          allData={allInvoice}
          setAllData={setAllInvoice}
        />
      )}

      {!loading ? (
        <TableContainer
          sx={{
            height: module_name !== "project-customer" && "calc(100vh - 245px)",
            overflowX: "auto",
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    style={{ minWidth: column.minWidth, ...column.style }}
                    sx={{
                      lineHeight: "0.9rem",
                    }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows?.map((row) => {
                const due_dateForCompare = moment(row?.due_date).format(
                  "YYYY-MM-DD",
                );
                const today = moment().format("YYYY-MM-DD");
                if (due_dateForCompare < today && row?.status !== "paid") {
                  bgColor = "#fff1f1";
                } else {
                  bgColor = "white";
                }
                return (
                  <>
                    <TableRow
                      hover
                      role="checkbox"
                      key={row.id}
                      style={{ backgroundColor: bgColor }}
                    >
                      {columns.map((column) => {
                        const value = row[column.id];
                        return (
                          <TableCell
                            key={column.id}
                            align={column.align}
                            style={{
                              padding: "10px 15px",
                            }}
                          >
                            {column.id === "date_added" ? (
                              value ? (
                                moment(value).format("ll")
                              ) : (
                                "-"
                              )
                            ) : column.id === "due_date" ? (
                              <DateColumn
                                id={row?.id}
                                value={value}
                                disabled={row?.status === "paid"}
                                setAllData={setAllInvoice}
                                onSubmit={(newDate) =>
                                  updateDueDate({
                                    id: row?.id,
                                    body_data: {
                                      due_date: newDate,
                                    },
                                  })
                                }
                                onSuccess={(newDate) => {
                                  setAllInvoice(
                                    allInvoice.map((invoice) =>
                                      invoice.id === row.id
                                        ? {
                                            ...row,
                                            due_date: newDate,
                                          }
                                        : invoice,
                                    ),
                                  );
                                }}
                              />
                            ) : column.id === "total" ? (
                              <Typography color="#7171ff" variant="body2">
                                {row.symbol + value}
                              </Typography>
                            ) : column.id === "due_payment" ? (
                              <Typography color="#ea6220" variant="body2">
                                {row.paid_amount
                                  ? `${row.symbol}${
                                      row.total - row.paid_amount
                                    }`
                                  : row.symbol + row.total}
                              </Typography>
                            ) : column.id === "paid_amount" ? (
                              <Typography color="#58d3e7" variant="body2">
                                {value ? `${row.symbol}${value}` : "-"}
                              </Typography>
                            ) : column.id === "status" ? (
                              <StatusColumn
                                id={row?.id}
                                value={value}
                                isPermit={permissions["change status"]}
                                options={invoiceStatuses}
                                setAllData={setAllInvoice}
                                onSubmit={(statusId) => ({
                                  method: "post",
                                  url: `/api/admin/invoice/change/status/${row?.id}`,
                                  data: { status: statusId },
                                })}
                                route="invoice"
                              />
                            ) : column.id === "assignees" ? (
                              <PopoverAG items={value} />
                            ) : column.id === "invoice_details" ? (
                              value?.length ? (
                                <Box
                                  sx={{
                                    position: "relative",
                                    cursor: "pointer",
                                    "&:hover": {
                                      "& svg": {
                                        color: (theme) =>
                                          theme.palette.primary.main,
                                      },
                                    },
                                    "& svg": {
                                      position: "absolute",
                                      color:
                                        openItems === row?.id
                                          ? (theme) =>
                                              theme.palette.primary.main
                                          : "#777",
                                      top: "0px",
                                      right: "0px",
                                      cursor: "pointer",
                                      fontSize: "17px",
                                    },
                                  }}
                                  onClick={() => {
                                    setOpenItems(
                                      openItems === row?.id ? "" : row?.id,
                                    );
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      width: "150px",
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        overflow: "hidden",
                                        WebkitLineClamp: "1",
                                        WebkitBoxOrient: "vertical",
                                        textOverflow: "ellipsis",
                                        width: "100px",
                                        maxHeight: "22px",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {`${value[0]?.item}`}
                                    </Typography>
                                    {value.length > 1 && (
                                      <Typography>
                                        & {value?.length - 1}+
                                      </Typography>
                                    )}
                                  </Box>
                                  <BsChevronExpand />
                                </Box>
                              ) : (
                                "-"
                              )
                            ) : column.id === "last_note" ? (
                              <Box
                                sx={{
                                  position: "relative",
                                  cursor: permissions?.note ? "pointer" : "",
                                  "&:hover": {
                                    "& svg": {
                                      color: (theme) =>
                                        permissions?.note
                                          ? theme.palette.primary.main
                                          : "#777",
                                    },
                                  },
                                  "& svg": {
                                    position: "absolute",
                                    color:
                                      openNote === row?.id
                                        ? (theme) => theme.palette.primary.main
                                        : "#777",
                                    top: "0px",
                                    right: "0px",
                                    cursor: permissions?.note ? "pointer" : "",
                                    fontSize: "17px",
                                  },
                                }}
                                onClick={() => {
                                  if (!permissions?.note) return;
                                  setOpenNote(
                                    openNote === row?.id ? "" : row?.id,
                                  );
                                }}
                              >
                                <Typography
                                  sx={{
                                    overflow: "hidden",
                                    WebkitLineClamp: "1",
                                    WebkitBoxOrient: "vertical",
                                    textOverflow: "ellipsis",
                                    maxWidth: "150px",
                                    maxHeight: "22px",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {value || "-"}
                                </Typography>
                                <MdEdit />
                              </Box>
                            ) : column.id === "action" ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: "8px",
                                  justifyContent: "center",
                                }}
                              >
                                <Tooltip title="View Payment">
                                  <IconButton
                                    onClick={() => setViewInvoice(row?.id)}
                                    disabled={!permissions?.show}
                                  >
                                    <TbListDetails />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Make Payment">
                                  <IconButton
                                    disabled={row.status === "paid"}
                                    onClick={() => handlePaymentBtn(row)}
                                  >
                                    <MdOutlinePayment />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Edit">
                                  <IconButton
                                    onClick={() => handleEditBtn(row)}
                                    disabled={!permissions?.update}
                                  >
                                    <Edit />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete">
                                  <IconButton
                                    onClick={() => handleDelete(row.id)}
                                    disabled={
                                      !permissions?.delete ||
                                      !permissions?.trash
                                    }
                                  >
                                    <Delete />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            ) : (
                              value
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                    <TableRow>
                      <TableCell
                        style={{ paddingBottom: 0, paddingTop: 0 }}
                        colSpan={columns?.length}
                      >
                        <Collapse
                          in={openItems === row?.id}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Box sx={{ margin: 1 }}>
                            <Box
                              sx={{
                                margin: "0 auto",
                                maxWidth: "580px",
                                zIndex: "-1",
                              }}
                            >
                              <Items
                                items={row.invoice_details}
                                symbol={row.symbol}
                              />
                            </Box>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <TableSkeleton columns={columns} tableHeight="calc(100vh - 245px)" />
      )}
      {module_name !== "project-customer" && (
        <TablePagination
          span={columns?.length}
          setReload={setReload}
          pagination={pagination}
          setPagination={setPagination}
        />
      )}
    </Fragment>
  );
};

export default InvoiceTable;
